import { useState, useEffect } from "react";
import 'primeicons/primeicons.css';
import { Link, Navigate, useNavigate } from "react-router-dom";
import { doSignInWithEmailAndPassword } from "../config/authUser";
import { useAuth } from "../config/authContext";
import { GoogleAuthProvider, signInWithPopup, getAdditionalUserInfo, onAuthStateChanged } from "firebase/auth";
import { auth } from "../config/firebase";
import Logotipo from "../assets/Icons/Logotipo.svg";
import GoogleIcon from "../assets/icons--medium--google@2x.png"
import '../styles/Auth.css';

export const Auth = () => {
    const { userLoggedIn } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isSigningIn, setIsSigningIn] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    
    // Detect if user is a new Google user (flag set during Google sign-up)
    // Only treat as new when loginMethod is google and the flag belongs to the current user
    const isNewGoogleUser = (() => {
        try {
            const method = localStorage.getItem('loginMethod');
            if (method !== 'google') return false;

            const raw = localStorage.getItem('NewUser');
            if (!raw) return false;

            const data = JSON.parse(raw);
            const currentEmail = auth.currentUser?.email;

            // If we don't have the current user yet, keep the flag true so Google flow can proceed
            if (!currentEmail) return true;

            return data?.email && data.email === currentEmail;
        } catch (_) {
            return false;
        }
    })();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            console.log("Auth state changed:", user ? `User authenticated: ${user.uid}` : "No user");
        });

        return () => unsubscribe();
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!isSigningIn) {
            setIsSigningIn(true);
            try {
                const userCredential = await doSignInWithEmailAndPassword(email, password);
                localStorage.setItem('loginMethod', 'email');
                console.log("Successfully signed in:", userCredential.user.uid);
                // For email/password accounts, require email verification first
                const user = userCredential.user;
                const providers = user.providerData || [];
                const isPassword = providers.some((p) => p.providerId === 'password');
                if (isPassword && !user.emailVerified) {
                    navigate('/verify-email');
                } else {
                    navigate('/dashboard');
                }
            } catch (error) {
                console.error("Sign-in error:", error);
                setErrorMessage(
                    error.code === 'auth/invalid-credential'
                        ? 'Invalid email or password'
                        : 'Failed to sign in'
                );
                setIsSigningIn(false);
            }
        }
    };

    const onGoogleSignIn = async (e) => {
        e.preventDefault();
        setIsSigningIn(true);
        try {
            const provider = new GoogleAuthProvider();
            provider.setCustomParameters({ prompt: 'select_account' });

            const result = await signInWithPopup(auth, provider);
            const additionalInfo = getAdditionalUserInfo(result);

            if (additionalInfo?.isNewUser) {
                const userData = {
                    loginMethod: 'google',
                    email: result.user.email
                };
                localStorage.setItem('NewUser', JSON.stringify(userData));
                localStorage.setItem('loginMethod', 'google');
                navigate('/questionnaire');
            } else {
                localStorage.setItem('loginMethod', 'google');
                navigate('/dashboard');
            }
        } catch (err) {
            console.error("Google sign-in error:", err);
            setIsSigningIn(false);
            setErrorMessage('Failed to sign in with Google');
        }
    };

    return (
        <div className="home">
            {userLoggedIn && (
                isNewGoogleUser ? (
                    <Navigate to={'/questionnaire'} replace={true} />
                ) : (
                    
                    auth.currentUser && auth.currentUser.providerData.some(p => p.providerId === 'password') && !auth.currentUser.emailVerified ? (
                        <Navigate to={'/verify-email'} replace={true} />
                    ) : (
                        <Navigate to={'/dashboard'} replace={true} />
                    )
                )
            )}

            {/* Navigation Bar */}
            <header className="navigation-presite-bar">
                <div className="logo-wrapper">
                    <Link to="/" className="logo">
                        Frazza
                    </Link>
                </div>
            </header>

            <div className="auth-layout">
                <div className="auth-left-container">
                    <div className="sub-logo">
                        <h1 className="main-heading">
                            Because Distance Shouldn't Mean Disconnection
                        </h1>
                        <p>Welcome back! Please login to your account.</p>
                    </div>

                    <div className="auth-container">
                        <div className="auth-box">
                            <form onSubmit={onSubmit} className="auth-form frazza-form">
                                <div className="frazza-form-group">
                                    <label className="frazza-form-label required">Email</label>
                                    <input 
                                        type="email" 
                                        className="frazza-input"
                                        autoComplete='email' 
                                        required 
                                        value={email} 
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="your@email.com"
                                    />
                                </div>
                                <div className="frazza-form-group">
                                    <label className="frazza-form-label required">Password</label>
                                    <input 
                                        type="password" 
                                        className="frazza-input"
                                        autoComplete='current-password' 
                                        required 
                                        value={password} 
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Enter your password"
                                    />
                                </div>
                                {errorMessage && <div className='frazza-form-error'>{errorMessage}</div>}
                                <button 
                                    type="submit" 
                                    disabled={isSigningIn} 
                                    className={`frazza-btn frazza-btn-primary frazza-btn-block ${isSigningIn ? 'loading' : ''}`}
                                >
                                    {isSigningIn ? 'Signing In...' : 'Sign In'}
                                </button>
                            </form>
                            <p className="auth-footer">Don't have an account? <Link to={'/register'}>Sign up</Link></p>
                            <div className='divider'>
                                <span>OR</span>
                            </div>
                            <button id="authButton" className={`google ${isSigningIn ? 'disabled' : ''}`} disabled={isSigningIn} onClick={onGoogleSignIn}>
                                <img src={GoogleIcon} alt="Google Icon" className="google-icon" />
                                {isSigningIn ? 'Signing In...' : 'Continue with Google'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Right Side - Logo Container */}
                <div className="logo-container">
                    <Link to="/">
                        <img src={Logotipo} alt="Logotipo de personas" className="logo-icon4" />
                    </Link>
                </div>
            </div>
        </div>
    );
};